import styled from "styled-components";
import { countries } from "countries-list";
import { useEffect, useState } from "react";
import * as CommonTypes from '../../../types/common.types'
import AllyService from "../../../services/ally.service";
import removeTimezone from "../../../utils/removeTimezone";
import { useGlobal } from "../../../context/global";
import { useAlly } from "../../../context/ally";
import m from "../../../styles/molanturTheme";
import { Avatar, Card, Dialog, Label, TourThumb, clp } from "../ui";

const countryName = (code?: string) => (code && (countries as Record<string, { name: string }>)[code]?.name) || code || ''

// Detalle de un huésped: sus datos, la salida que le asignó la agencia (si ya la hay) y todos sus
// tours. Sin ids de la base: al aliado no le sirven.
export default function TripInfo() {
    const { setIsLoading } = useGlobal()
    const {tripInfo, setTripInfo} = useAlly()
    const [plan, setPlan] = useState<CommonTypes.PassengerPlan[]>([])
    useEffect(() => {
        async function getPlan() {
            try {
                if (!tripInfo?.passenger?.id) return
                setIsLoading(true)
                const response = (await AllyService.getPassengerPlan(tripInfo.passenger?.id)).data
                setPlan(response)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        getPlan()
    }, [])

    const passenger = tripInfo?.passenger
    const trip = tripInfo?.trip
    const hasTrip = !!trip?.id
    const row = (label: string, value?: string | number | null) => value ? <div><dt>{label}</dt><dd>{value}</dd></div> : null

    return (
        <Dialog title="Detalle del huésped" onClose={() => setTripInfo(undefined)}>
            <Main>
                <Card $pad={14}>
                    <div className="head">
                        <Avatar name={passenger?.name || ''} size={42} />
                        <div>
                            <strong>{passenger?.name}</strong>
                            <span>{countryName(passenger?.nationality)}</span>
                        </div>
                    </div>
                    <dl>
                        {row('Email', passenger?.email)}
                        {row('Teléfono', passenger?.phone)}
                        {row('Hotel', passenger?.hotel)}
                        {row('RUT / Pasaporte', passenger?.rut_passport)}
                        {row('Remarks', passenger?.remark)}
                    </dl>
                </Card>

                <Label className="section">Salida de este tour</Label>
                <Card $pad={14}>
                    {!hasTrip ? <p className="waiting">La agencia todavía no asigna la salida.</p> : (
                        <dl className="flush">
                            {row('Fecha', trip?.trip_date ? removeTimezone(String(trip.trip_date)) : '')}
                            {row('Hora', trip?.trip_time ? String(trip.trip_time).slice(0, 5) : '')}
                            {row('Conductor', trip?.driver?.name)}
                            {row('Guía', trip?.guider?.name)}
                        </dl>
                    )}
                </Card>

                {plan.length > 0 && <Label className="section">Todos sus tours</Label>}
                {
                    plan.map((item, idx) => {
                        const date = removeTimezone(item.reservation_date.toString())
                        return (
                            <Card key={idx} $pad={12} className="plan">
                                <TourThumb tourId={item.tour_id} />
                                <div>
                                    <strong>{item.tour_id}</strong>
                                    <span>{date}</span>
                                    <span className="meta">
                                        Tour {clp(item.price || 0)} · Entradas {clp(item.tickets || 0)}
                                        {item.trip_id ? ' · Salida asignada' : ''}
                                    </span>
                                </div>
                            </Card>
                        )
                    })
                }
            </Main>
        </Dialog>
    )
}

const Main = styled.div`
    .head {
        display: flex;
        align-items: center;
        gap: 12px;
        strong {
            display: block;
            font-weight: 600;
        }
        span {
            font-size: 12.5px;
            color: ${m.color.inkFaint};
        }
    }
    dl {
        margin: 10px 0 0;
        > div {
            display: flex;
            justify-content: space-between;
            gap: 14px;
            padding: 7px 0;
            border-top: 1px solid ${m.color.line};
        }
    }
    dl.flush {
        margin: 0;
        > div:first-child {
            border-top: none;
            padding-top: 0;
        }
        > div:last-child { padding-bottom: 0; }
    }
    dt {
        flex-shrink: 0;
        font-size: 13.5px;
        color: ${m.color.inkSoft};
    }
    dd {
        margin: 0;
        min-width: 0;
        text-align: right;
        font-size: 13.5px;
        font-weight: 600;
        overflow-wrap: anywhere;
    }
    .section { margin: 18px 0 8px; }
    .waiting {
        margin: 0;
        font-size: 13.5px;
        color: ${m.color.inkFaint};
    }
    .plan {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;
        > div {
            min-width: 0;
            display: flex;
            flex-direction: column;
            font-size: 13px;
        }
        strong {
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 16px;
        }
    }
    .meta {
        font-size: 12.5px;
        color: ${m.color.inkFaint};
    }
`
